import Image from 'next/image'

import { claimBonus } from '@/api/v2/queries/rewards'
import { type BonusType, type MyUser, type RewardsInfo } from '@/api/v2/types'

import { useRewardResultModalStore } from '@/store/modal/reward'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'

import Button from '@/components/ui/button'
import { Text } from '@/components/ui/text'
import { toast } from '@/components/ui/toast'
import { Flex } from '@/styled-system/jsx'

const boxColors = {
  rakeback: 'green',
  daily: 'blue',
  weekly: 'yellow',
  monthly: 'red',
}

export default function HeaderRewardsDropdownItem({
  type,
  title,
  reward,
  canClaim,
  onClaim,
}: {
  type: BonusType
  title: string
  reward: number
  canClaim?: boolean
  onClaim?: () => void
}) {
  const t = useTranslations('header.rewards-dropdown')
  const queryClient = useQueryClient()
  const {
    setIsOpen,
    setTitle,
    setBalancePercentage,
    setCalendarPercentage,
    setAmountUsd,
  } = useRewardResultModalStore((state) => state)
  const { mutate, isLoading } = useMutation(claimBonus, {
    onSuccess: (data) => {
      onClaim?.()
      setTitle(title)
      setAmountUsd(reward)
      if (type === 'rakeback') {
        setBalancePercentage(1)
        setCalendarPercentage(0)
      } else {
        setBalancePercentage(1)
        setCalendarPercentage(0)
      }
      setIsOpen()
      queryClient.setQueryData<MyUser>(['user'], (oldData) =>
        oldData
          ? {
              ...oldData,
              balanceUsd: data.balance,
            }
          : oldData
      )

      queryClient.setQueryData<RewardsInfo>(['rewards'], (oldData) => {
        if (!oldData) {
          return
        }

        const newData = { ...oldData }

        switch (type) {
          case 'rakeback':
            newData.userRakeBackAmount = 0
            break
          case 'daily':
            newData.userDailyBonusAmount = 0
            break
          case 'weekly':
            newData.userWeeklyBonusAmount = 0
            break
          case 'monthly':
            newData.userMonthlyBonusAmount = 0
            break
        }

        return newData
      })
    },
    onError: (error: any) => {
      toast.create({
        title: `Error`,
        description: error.response.data.message,
        type: 'error',
      })
    },
  })

  const handleClaim = () => {
    mutate({ type })
  }

  const isDisabled = !canClaim || reward < 0.01

  return (
    <Flex
      align='center'
      justify='space-between'
      gap={5}
      py={1}
      px={6}
      position='relative'
      minW='338px'
      zIndex='0'
      {...(isDisabled
        ? {
            borderTop: '1px solid #2E2232',
          }
        : {
            borderRadius: 'lg',
            _before: {
              content: "''",
              bg: 'rgba(233, 0, 131, 0.10)',
              borderColor: 'primary',
              borderWidth: '1px',
              borderRadius: 'lg',
              maskImage: 'linear-gradient(90deg, white 0%, transparent 100%)',
              position: 'absolute',
              inset: 'auto',
              top: '0',
              left: '0',
              h: 'full',
              w: 'full',
              zIndex: '-1',
            },
          })}
    >
      <Flex
        justify='center'
        direction='column'
        gap={3.5}
        textTransform='uppercase'
      >
        <Text as='span' color='#A997B5' textStyle='sm' fontWeight='semibold'>
          {t('bonus')}{' '}
          <Text as='b' color='primary' fontSize='inherit' fontWeight='bold'>
            {t(`${type}-item.title`)}
          </Text>
        </Text>

        <Button
          variant={
            isLoading || !canClaim || reward < 0.01 ? 'outline' : 'secondary'
          }
          size='xs'
          py='3'
          px='2'
          h='38px'
          minW='102px'
          w='fit'
          loading={isLoading}
          disabled={isLoading || !canClaim || reward < 0.01}
          onClick={handleClaim}
        >
          {canClaim && reward >= 0.01
            ? `${t('monthly-item.button-text')} ${reward?.toFixed(2)}`
            : t('monthly-item.button-text')}
        </Button>
      </Flex>

      <Image
        src={`/box_${boxColors[type]}.webp/height=120`}
        alt='Reward Box'
        width={100}
        height={90}
      />
    </Flex>
  )
}
